import { useMemo } from "react"
import { useParams } from "react-router-dom"
import { useTranslation } from "providers/i18n"
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  CardMedia,
  IconButton,
  List,
  Typography,
  useTheme,
  Button,
} from "@mui/material"
import LanguageIcon from "@mui/icons-material/Language"

import { usePromotionDetail } from "hooks/promotion/usePromotionDetail"

import { PageLayout } from "layouts/PageLayout"

import { ShareIconButton } from "components/Button"
import { CalendarIcon, LocationIcon } from "components/icons"
import { Head } from "components/Head"
import { InfoListItem, InfoListItemWithNavigation } from "components/ListItem"
import { LocationInfo } from "components/domains/features/LocationInfo"
import { Carousel, CarouselItem } from "components/Carousel"
import { ScrollTrigger } from "components/ScrollTrigger"

import { getIcon } from "utils/icon"

export const PromotionDetail = ({ Layout = PageLayout, AppNavigation }) => {
  const params = useParams()
  const { promotionId } = params
  const { t } = useTranslation()
  const { promotion, navigateToLocation, openWebsiteLink } =
    usePromotionDetail(promotionId)
  const theme = useTheme()
  const styleOverrides = theme.components[`VuiDetailCard`]?.styleOverrides || {}
  const {
    card,
    infoListItem,
    infoListItemWithNavigation,
    cardContent,
    cardHeader,
    cardHeaderAction,
    cardHeaderActionButton,
    viewLocationContainer,
    viewLocationButton,
    scrollTriggerWrapper,
    cardContentPrimaryText,
    cardContentSecondaryText,
    galleryCarouselWrapper,
    galleryCarousel,
    galleryCarouselItem,
    pageLayout,
  } = styleOverrides

  const locationIcon = useMemo(
    () => getIcon(theme?.assets?.locationIcon) || <LocationIcon />,
    [theme]
  )

  if (!promotion) return null

  return (
    <>
      <Head title={`${t(promotion.name)} (${t("Promotion Detail")})`} />
      <Layout sx={pageLayout} AppNavigation={AppNavigation}>
        <Card elevation={0} sx={card}>
          {promotion.allImages && (
            <Box sx={galleryCarouselWrapper}>
              <Carousel
                options={{
                  type: "loop",
                  padding: 0,
                  gap: 8,
                  pagination: true,
                  autoplay: true,
                }}
                sx={galleryCarousel}
              >
                {promotion.allImages.map(({ id, url }) => {
                  return (
                    <CarouselItem key={`slide-${id}`} sx={galleryCarouselItem}>
                      <CardMedia
                        component="img"
                        sx={galleryCarouselItem}
                        src={url as string}
                      />
                    </CarouselItem>
                  )
                })}
              </Carousel>
            </Box>
          )}
          <CardHeader
            title={t(promotion.name)}
            action={
              <Box sx={cardHeaderAction}>
                {!!promotion.websiteLink && (
                  <IconButton
                    className="website-button"
                    color="primary"
                    // * URL should be prepended with protocol (e.g. "https://") for the URL to be replaced
                    onClick={openWebsiteLink}
                    sx={cardHeaderActionButton}
                  >
                    <LanguageIcon />
                  </IconButton>
                )}
                <ShareIconButton
                  className="share-button"
                  url={window.location.href}
                  sx={cardHeaderActionButton}
                  title={t("ShareButton.title")}
                />
              </Box>
            }
            sx={cardHeader}
          />
          {promotion.hasLocation && (
            <Box sx={viewLocationContainer}>
              <Button
                variant="contained"
                startIcon={locationIcon}
                onClick={navigateToLocation}
                sx={viewLocationButton}
              >
                {t("View Location")}
              </Button>
            </Box>
          )}
          <List>
            <InfoListItem
              sx={infoListItem}
              icon={<CalendarIcon />}
              primary={promotion.durationDate}
              secondary={promotion.openingText}
            />
            {promotion.hasLocation && (
              <InfoListItemWithNavigation
                sx={infoListItemWithNavigation}
                icon={<LocationIcon />}
                primary={t("Navigate")}
                secondary={
                  <LocationInfo feature={promotion.feature} component="span" />
                }
                onClick={navigateToLocation}
              />
            )}
          </List>
          <CardContent sx={cardContent}>
            <Typography
              variant="body1"
              gutterBottom
              sx={cardContentPrimaryText}
            >
              {t("Promotion Detail")}
            </Typography>
            <Typography
              variant="body2"
              sx={cardContentSecondaryText}
              dangerouslySetInnerHTML={{
                __html: promotion.descriptionText,
              }}
            ></Typography>
          </CardContent>
        </Card>
      </Layout>
      {promotion.hasLocation && (
        <ScrollTrigger>
          <Box sx={scrollTriggerWrapper}>
            <Box sx={viewLocationContainer}>
              <Button
                variant="contained"
                startIcon={locationIcon}
                onClick={navigateToLocation}
                sx={viewLocationButton}
              >
                {t("View Location")}
              </Button>
            </Box>
          </Box>
        </ScrollTrigger>
      )}
    </>
  )
}
